import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Grid, IconButton, MenuItem,
  Pagination, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import { ReceiptLong, Business, Download, Save, CreditCard } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import billingService, { INVOICE_TYPE_LABELS, INVOICE_STATUS_LABELS, formatHuf } from '../../services/billingService';
import paymentService from '../../services/paymentService';

const TAX_TYPE_OPTIONS = [
  { value: 'HU_COMPANY', label: 'Magyar cég / egyéni vállalkozó (adószámmal)' },
  { value: 'PRIVATE_PERSON', label: 'Magánszemély (adószám nélkül)' },
  { value: 'EU_COMPANY', label: 'EU-s cég (közösségi adószámmal)' },
  { value: 'NON_EU_COMPANY', label: 'EU-n kívüli cég' },
];

const EMPTY_PROFILE = {
  name: '', taxType: 'HU_COMPANY', taxNumber: '', euTaxNumber: '',
  countryCode: 'HU', postCode: '', city: '', address: '', email: '',
};

const PAGE_SIZE = 10;

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('hu-HU') : '–');

/**
 * Szolgáltatói "Számlázás" fül: a Wedigo által a szolgáltatónak kiállított számlák vevőadatai
 * (ProviderBillingProfile) és a számlák listája PDF-letöltéssel.
 */
const ProviderBillingTab = () => {
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [complete, setComplete] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [message, setMessage] = useState(null);

  const [invoices, setInvoices] = useState([]);
  const [invoicePage, setInvoicePage] = useState(1);
  const [invoiceTotal, setInvoiceTotal] = useState(0);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [invoiceError, setInvoiceError] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [payingId, setPayingId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    (async () => {
      try {
        const res = await billingService.getProfile();
        if (res.data) {
          setProfile({ ...EMPTY_PROFILE, ...Object.fromEntries(Object.entries(res.data).map(([k, v]) => [k, v ?? ''])) });
        }
        setComplete(!!res.complete);
      } catch (err) {
        setMessage({ severity: 'error', text: err.message || 'A számlázási adatok betöltése sikertelen.' });
      } finally {
        setProfileLoading(false);
      }
    })();
  }, []);

  const loadInvoices = useCallback(async () => {
    setInvoicesLoading(true);
    setInvoiceError(null);
    try {
      const res = await billingService.getMyInvoices({ page: invoicePage, limit: PAGE_SIZE });
      setInvoices(res.data || []);
      setInvoiceTotal(res.pagination?.total || 0);
    } catch (err) {
      setInvoiceError(err.message || 'A számlák betöltése sikertelen.');
    } finally {
      setInvoicesLoading(false);
    }
  }, [invoicePage]);

  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  // Visszatérés a Barion fizetőoldalról (?invoicePayment=<paymentId>): állapot lekérése, lista frissítése
  useEffect(() => {
    const paymentId = searchParams.get('invoicePayment');
    if (!paymentId) return;
    (async () => {
      try {
        const res = await paymentService.getPaymentStatus(paymentId);
        const status = res.data?.status;
        setInvoiceError(null);
        if (status === 'SUCCEEDED') setMessage({ severity: 'success', text: 'Köszönjük, a jutalékszámla kifizetése sikeres.' });
        else if (status === 'PENDING') setMessage({ severity: 'info', text: 'A fizetés feldolgozása folyamatban — pár perc múlva frissítsd az oldalt.' });
        else setMessage({ severity: 'error', text: 'A fizetés nem sikerült vagy megszakadt. Próbáld újra.' });
      } catch (err) {
        setMessage({ severity: 'error', text: err.message || 'A fizetés állapota nem kérdezhető le.' });
      } finally {
        loadInvoices();
        const next = new URLSearchParams(searchParams);
        next.delete('invoicePayment');
        next.delete('paymentId');
        setSearchParams(next, { replace: true });
      }
    })();
  }, [searchParams, setSearchParams, loadInvoices]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setProfile(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'taxType' && value === 'HU_COMPANY') next.countryCode = 'HU';
      return next;
    });
    setFieldErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    setFieldErrors({});
    try {
      await billingService.updateProfile(profile);
      setComplete(true);
      setMessage({ severity: 'success', text: 'Számlázási adatok mentve.' });
    } catch (err) {
      if (err.data?.errors) setFieldErrors(err.data.errors);
      setMessage({ severity: 'error', text: err.message || 'A mentés sikertelen.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (invoice) => {
    setDownloadingId(invoice.id);
    setInvoiceError(null);
    try {
      await billingService.downloadMyInvoicePdf(invoice);
    } catch (err) {
      setInvoiceError(err.message);
    } finally {
      setDownloadingId(null);
    }
  };

  const handlePay = async (invoice) => {
    setPayingId(invoice.id);
    setInvoiceError(null);
    try {
      const res = await billingService.payMyInvoice(invoice.id);
      if (res.data?.paymentUrl) {
        window.location.href = res.data.paymentUrl;
        return;
      }
      setInvoiceError(res.message || 'A fizetés indítása sikertelen.');
    } catch (err) {
      setInvoiceError(err.message || 'A fizetés indítása sikertelen.');
    }
    setPayingId(null);
  };

  const field = (name, label, props = {}) => (
    <TextField
      fullWidth
      size="small"
      label={label}
      value={profile[name]}
      onChange={handleChange(name)}
      error={!!fieldErrors[name]}
      helperText={fieldErrors[name] || props.helperText}
      {...props}
    />
  );

  const { taxType } = profile;

  return (
    <>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Business sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" color="primary.dark">Számlázási adatok</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ezekre az adatokra állítjuk ki a számlát az előfizetésről, kiemelésről és a platform jutalékáról.
            Kiemelés vásárlása vagy online fizetés elfogadása előtt kötelező kitölteni.
          </Typography>

          {!profileLoading && !complete && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              A számlázási adataid még hiányoznak — enélkül nem tudunk számlát kiállítani.
            </Alert>
          )}

          {profileLoading ? (
            <Skeleton variant="rectangular" height={260} />
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {field('taxType', 'Vevő típusa', {
                  select: true,
                  children: TAX_TYPE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>),
                })}
              </Grid>
              <Grid item xs={12}>
                {field('name', taxType === 'PRIVATE_PERSON' ? 'Név' : 'Cégnév / számlázási név')}
              </Grid>
              {taxType === 'HU_COMPANY' && (
                <>
                  <Grid item xs={12} sm={6}>
                    {field('taxNumber', 'Adószám', { placeholder: '12345678-1-12' })}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {field('euTaxNumber', 'Közösségi adószám (ha van)', { placeholder: 'HU12345678' })}
                  </Grid>
                </>
              )}
              {taxType === 'EU_COMPANY' && (
                <Grid item xs={12} sm={6}>
                  {field('euTaxNumber', 'Közösségi adószám', { placeholder: 'DE123456789' })}
                </Grid>
              )}
              {taxType === 'NON_EU_COMPANY' && (
                <Grid item xs={12} sm={6}>
                  {field('taxNumber', 'Adóazonosító (ha van)')}
                </Grid>
              )}
              {taxType !== 'HU_COMPANY' && (
                <Grid item xs={12} sm={6}>
                  {field('countryCode', 'Országkód', { placeholder: 'HU', inputProps: { maxLength: 2 } })}
                </Grid>
              )}
              <Grid item xs={12} sm={4}>
                {field('postCode', 'Irányítószám')}
              </Grid>
              <Grid item xs={12} sm={8}>
                {field('city', 'Település')}
              </Grid>
              <Grid item xs={12}>
                {field('address', 'Utca, házszám')}
              </Grid>
              <Grid item xs={12}>
                {field('email', 'Számlázási email cím', { type: 'email', helperText: 'Ide küldjük az elektronikus számlákat.' })}
              </Grid>
            </Grid>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Save />}
              onClick={handleSave}
              disabled={saving || profileLoading}
            >
              {saving ? 'Mentés...' : 'Mentés'}
            </Button>
            {message && <Alert severity={message.severity} sx={{ flex: 1 }}>{message.text}</Alert>}
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <ReceiptLong sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" color="primary.dark">Számláim</Typography>
          </Box>

          {invoiceError && <Alert severity="error" sx={{ mb: 2 }}>{invoiceError}</Alert>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Az offline (nem online) fizetett foglalások jutalékáról havonta gyűjtőszámlát küldünk, a következő hónap
            10-ei határidővel. Fizethetsz átutalással (közleményben a számlaszám) vagy online bankkártyával.
          </Typography>

          {invoicesLoading ? (
            <Skeleton variant="rectangular" height={120} />
          ) : invoices.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Még nincs számlád.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Számlaszám</TableCell>
                    <TableCell>Tárgy</TableCell>
                    <TableCell>Kelt</TableCell>
                    <TableCell>Fizetési határidő</TableCell>
                    <TableCell align="right">Bruttó</TableCell>
                    <TableCell>Állapot</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map(invoice => {
                    const status = INVOICE_STATUS_LABELS[invoice.status] || INVOICE_STATUS_LABELS.PENDING;
                    return (
                      <TableRow key={invoice.id}>
                        <TableCell>
                          {invoice.invoiceNumber || '–'}
                          {invoice.stornoInvoiceNumber && (
                            <Typography variant="caption" color="text.secondary" display="block">sztornó: {invoice.stornoInvoiceNumber}</Typography>
                          )}
                        </TableCell>
                        <TableCell>{INVOICE_TYPE_LABELS[invoice.type] || invoice.type}</TableCell>
                        <TableCell>{formatDate(invoice.issuedAt || invoice.createdAt)}</TableCell>
                        <TableCell>{invoice.paid ? 'Kifizetve' : formatDate(invoice.dueDate)}</TableCell>
                        <TableCell align="right">{formatHuf(invoice.grossAmount)}</TableCell>
                        <TableCell><Chip size="small" label={status.label} color={status.color} /></TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {invoice.status === 'ISSUED' && !invoice.paid && invoice.type === 'MONTHLY_COMMISSION' && (
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={payingId === invoice.id ? <CircularProgress size={16} color="inherit" /> : <CreditCard />}
                              disabled={payingId === invoice.id}
                              onClick={() => handlePay(invoice)}
                              sx={{ mr: 1 }}
                            >
                              Fizetés online
                            </Button>
                          )}
                          {invoice.status === 'ISSUED' && (
                            <Tooltip title="PDF letöltése">
                              <span>
                                <IconButton size="small" onClick={() => handleDownload(invoice)} disabled={downloadingId === invoice.id}>
                                  {downloadingId === invoice.id ? <CircularProgress size={18} /> : <Download fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {invoiceTotal > PAGE_SIZE && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination count={Math.ceil(invoiceTotal / PAGE_SIZE)} page={invoicePage} onChange={(_, p) => setInvoicePage(p)} />
            </Box>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default ProviderBillingTab;
